import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { TrendingUp, Wallet, Calendar, Star, Users, Award, Radio, Phone, Video, MessageCircle, DollarSign } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ProviderDashboard() {
  const { user, refreshMe } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [earnings, setEarnings] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [profile, setProfile] = useState(null);
  const [isOnline, setIsOnline] = useState(false);
  const [wallet, setWallet] = useState({ balance: 0, transactions: [] });

  useEffect(() => {
    if (user === false) { nav("/login"); return; }
    if (!user) return;
    if (!["astrologer", "pandit"].includes(user.role)) { nav("/dashboard"); return; }
    (async () => {
      try {
        const [e, b, m, w] = await Promise.all([
          api.get("/provider/earnings"),
          api.get("/bookings"),
          api.get("/provider/me"),
          api.get("/wallet"),
        ]);
        setEarnings(e.data);
        setBookings(b.data);
        setProfile(m.data.profile);
        setIsOnline(m.data.profile?.is_online ?? false);
        setWallet(w.data);
      } catch (err) { console.error(err); }
    })();
  }, [user, nav]);

  if (!user || user === false) return <div className="p-12 text-center text-[#A0A4AB]">Loading…</div>;
  if (!earnings) return <div className="p-12 text-center text-[#A0A4AB]">Loading your studio…</div>;

  const toggleAvailability = async () => {
    const next = !isOnline;
    setIsOnline(next);
    try {
      await api.post("/provider/availability", { is_online: next });
      toast.success(next ? "You are LIVE for consultations 🌟" : "You are offline");
    } catch { setIsOnline(!next); toast.error("Failed"); }
  };

  const withdraw = async (amount) => {
    try {
      await api.post("/wallet/txn", { amount, kind: "debit", note: "Withdrawal to bank" });
      setWallet((await api.get("/wallet")).data);
      await refreshMe();
      toast.success(`₹${amount} withdrawal request placed`);
    } catch (e) { toast.error(e.response?.data?.detail || "Insufficient balance"); }
  };

  const isAstro = user.role === "astrologer";
  const TABS = [
    { key: "overview", label: "Overview", icon: TrendingUp },
    { key: "bookings", label: isAstro ? "Sessions" : "Poojas", icon: Calendar },
    { key: "earnings", label: "Earnings", icon: DollarSign },
    { key: "wallet", label: "Wallet", icon: Wallet },
  ];

  const Stat = ({ label, value, sub, accent }) => (
    <div className={`p-6 rounded-xl border ${accent || "border-white/10 bg-[#0A0C10]"}`}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]/80">{label}</div>
      <div className="font-serif text-3xl mt-2">{value}</div>
      {sub && <div className="text-xs text-[#A0A4AB] mt-1">{sub}</div>}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 pb-24 lg:pb-12">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 flex-wrap mb-8">
        <div className="flex items-center gap-4">
          {profile?.photo && (
            <div className="w-16 h-16 rounded-full overflow-hidden border border-[#D4AF37]/40">
              <img src={profile.photo} className="w-full h-full object-cover" alt=""/>
            </div>
          )}
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-1">
              {isAstro ? "Astrologer Studio" : "Pandit Studio"}
            </div>
            <h1 className="font-serif text-4xl">Namaste, <span className="gold-gradient">{user.name?.split(" ").slice(-1)[0]}</span></h1>
            <div className="flex items-center gap-3 mt-2">
              {profile && (
                <>
                  <span className="text-xs text-[#A0A4AB] flex items-center gap-1"><Star size={12} className="text-[#D4AF37]" fill="#D4AF37"/> {profile.rating}</span>
                  <span className="text-xs text-[#A0A4AB]">·</span>
                  <span className="text-xs text-[#A0A4AB]">{profile.sessions}+ sessions</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Availability toggle */}
        <button
          data-testid="availability-toggle"
          onClick={toggleAvailability}
          className={`inline-flex items-center gap-3 px-5 py-3 rounded-full border transition ${
            isOnline
              ? "border-emerald-400/60 bg-emerald-400/10 text-emerald-400"
              : "border-white/15 text-[#A0A4AB] hover:border-white/30"
          }`}
        >
          <span className={`w-2 h-2 rounded-full ${isOnline ? "bg-emerald-400 animate-pulse" : "bg-[#6E737D]"}`}/>
          {isOnline ? "Online for consultations" : "Currently offline"}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 mb-8 overflow-x-auto">
        {TABS.map((t) => {
          const Ic = t.icon;
          return (
            <button
              key={t.key}
              data-testid={`provider-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-5 py-3 text-sm inline-flex items-center gap-2 border-b-2 transition whitespace-nowrap ${
                tab === t.key ? "border-[#D4AF37] text-[#D4AF37]"
                              : "border-transparent text-[#A0A4AB] hover:text-[#EAE6DF]"
              }`}
            >
              <Ic size={14}/> {t.label}
            </button>
          );
        })}
      </div>

      {/* OVERVIEW */}
      {tab === "overview" && (
        <div className="space-y-6">
          <div className="grid md:grid-cols-4 gap-4">
            <Stat label="This Month Net" value={`₹${earnings.month_net.toFixed(0)}`} sub={`${earnings.month_sessions} sessions`} accent="border-[#D4AF37]/40 bg-gradient-to-br from-[#12151C] to-[#0A0C10]"/>
            <Stat label="Wallet Balance" value={`₹${wallet.balance.toFixed(0)}`} sub="Available to withdraw"/>
            <Stat label="All-time Net" value={`₹${earnings.total_net.toFixed(0)}`} sub={`After ${earnings.commission_pct}% platform`}/>
            <Stat label="Total Sessions" value={earnings.total_sessions}/>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2 p-6 rounded-xl bg-[#0A0C10] border border-white/10">
              <div className="font-serif text-xl mb-4">Recent Sessions</div>
              {bookings.length === 0 ? (
                <div className="text-sm text-[#A0A4AB]">No sessions yet. Set yourself online to receive bookings.</div>
              ) : (
                <div className="space-y-2">
                  {bookings.slice(0, 5).map((b) => (
                    <div key={b.id} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center text-xs text-[#D4AF37]">
                          {(b.customer_name || "?")[0]}
                        </div>
                        <div>
                          <div className="text-sm">{b.customer_name || "Seeker"}</div>
                          <div className="text-xs text-[#A0A4AB] capitalize">{b.service_type.replace("_"," ")} · {new Date(b.scheduled_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-emerald-400">+₹{b.provider_earning?.toFixed(0) || 0}</div>
                        <div className="text-[10px] text-[#6E737D]">of ₹{b.amount}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 rounded-xl bg-[#0A0C10] border border-white/10">
              <div className="font-serif text-xl mb-4">Consultation Modes</div>
              <div className="space-y-3">
                {(isAstro ? [
                  { icon: MessageCircle, label: "Chat", note: "Text session" },
                  { icon: Phone, label: "Voice", note: "Audio call" },
                  { icon: Video, label: "Video", note: "Face-to-face" },
                ] : [
                  { icon: Radio, label: "Pooja", note: "Live/On-site" },
                  { icon: Radio, label: "Yagya", note: "Havan ritual" },
                  { icon: Radio, label: "Path", note: "Recitation" },
                ]).map((m, i) => {
                  const Ic = m.icon;
                  return (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-[#12151C] border border-white/5">
                      <div className="w-9 h-9 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                        <Ic size={14} className="text-[#D4AF37]"/>
                      </div>
                      <div>
                        <div className="text-sm">{m.label}</div>
                        <div className="text-xs text-[#A0A4AB]">{m.note}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {profile && (
                <div className="mt-5 pt-5 border-t border-white/10">
                  <div className="text-[10px] uppercase tracking-widest text-[#A0A4AB]">Your Rate</div>
                  <div className="font-serif text-2xl text-[#D4AF37] mt-1">
                    {isAstro ? `₹${profile.rate_per_min}/min` : "₹2,100+ / pooja"}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* BOOKINGS */}
      {tab === "bookings" && (
        <div className="p-6 rounded-xl bg-[#0A0C10] border border-white/10">
          {bookings.length === 0 ? (
            <div className="py-12 text-center text-[#A0A4AB]">
              No sessions booked yet.<br/>
              <span className="text-xs">Turn on availability to receive new bookings.</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
                <thead>
                  <tr className="text-left border-b border-white/10">
                    <th className="py-3 text-xs uppercase tracking-widest text-[#A0A4AB]">Customer</th>
                    <th className="py-3 text-xs uppercase tracking-widest text-[#A0A4AB]">Service</th>
                    <th className="py-3 text-xs uppercase tracking-widest text-[#A0A4AB]">When</th>
                    <th className="py-3 text-xs uppercase tracking-widest text-[#A0A4AB]">Gross</th>
                    <th className="py-3 text-xs uppercase tracking-widest text-[#A0A4AB]">You Earn</th>
                    <th className="py-3 text-xs uppercase tracking-widest text-[#A0A4AB]">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-b border-white/5 last:border-0">
                      <td className="py-3">{b.customer_name || "—"}</td>
                      <td className="py-3 capitalize text-[#A0A4AB]">{b.service_type.replace("_"," ")}</td>
                      <td className="py-3 text-[#A0A4AB]">{new Date(b.scheduled_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="py-3">₹{b.amount}</td>
                      <td className="py-3 text-emerald-400">₹{b.provider_earning?.toFixed(0)}</td>
                      <td className="py-3"><span className="px-2 py-1 rounded-full text-xs bg-[#12151C] border border-white/10 capitalize">{b.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* EARNINGS */}
      {tab === "earnings" && (
        <div className="space-y-6">
          <div className="grid md:grid-cols-4 gap-4">
            <Stat label="Gross Revenue" value={`₹${earnings.total_gross.toFixed(0)}`}/>
            <Stat label={`Platform Fee (${earnings.commission_pct}%)`} value={`- ₹${earnings.total_commission.toFixed(0)}`}/>
            <Stat label="Net Earnings" value={`₹${earnings.total_net.toFixed(0)}`} accent="border-emerald-400/40 bg-emerald-400/5"/>
            <Stat label="Sessions" value={earnings.total_sessions}/>
          </div>

          <div className="p-6 rounded-xl bg-[#0A0C10] border border-white/10">
            <div className="font-serif text-xl mb-2">Commission Breakdown</div>
            <p className="text-sm text-[#A0A4AB] mb-4">
              For every consultation, Vedacosmos charges a flat <span className="text-[#D4AF37]">{earnings.commission_pct}%</span> platform fee.
              You keep <span className="text-emerald-400">{100 - earnings.commission_pct}%</span> which is instantly credited to your wallet.
            </p>
            <div className="flex h-4 rounded-full overflow-hidden">
              <div className="bg-emerald-400" style={{ width: `${100 - earnings.commission_pct}%` }}/>
              <div className="bg-[#D4AF37]" style={{ width: `${earnings.commission_pct}%` }}/>
            </div>
            <div className="flex justify-between text-xs mt-2">
              <span className="text-emerald-400">You · {100 - earnings.commission_pct}%</span>
              <span className="text-[#D4AF37]">Platform · {earnings.commission_pct}%</span>
            </div>
          </div>
        </div>
      )}

      {/* WALLET */}
      {tab === "wallet" && (
        <div className="grid md:grid-cols-12 gap-4">
          <div className="md:col-span-5 p-8 rounded-xl bg-gradient-to-br from-emerald-500/10 to-[#0A0C10] border border-emerald-400/30">
            <div className="text-xs uppercase tracking-[0.3em] text-emerald-400">Provider Wallet</div>
            <div className="font-serif text-5xl mt-3 text-emerald-400">₹{wallet.balance.toFixed(2)}</div>
            <div className="text-xs text-[#A0A4AB] mt-2">Available to withdraw</div>
            <div className="grid grid-cols-3 gap-2 mt-6">
              {[500, 1000, 5000].map((a) => (
                <button
                  key={a}
                  data-testid={`withdraw-${a}`}
                  onClick={() => withdraw(a)}
                  disabled={wallet.balance < a}
                  className="py-2 rounded-full border border-white/15 text-sm hover:border-emerald-400 hover:text-emerald-400 transition disabled:opacity-40"
                >
                  Withdraw ₹{a}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-[#6E737D] mt-3">Demo mode — instant simulated withdrawal.</div>
          </div>

          <div className="md:col-span-7 p-6 rounded-xl bg-[#0A0C10] border border-white/10">
            <div className="font-serif text-xl mb-4">Recent Transactions</div>
            {wallet.transactions.length === 0 ? (
              <div className="text-sm text-[#A0A4AB]">No transactions yet.</div>
            ) : (
              <div className="space-y-2">
                {wallet.transactions.map((t) => (
                  <div key={t.id} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                    <div>
                      <div className="text-sm">{t.note || t.kind}</div>
                      <div className="text-xs text-[#A0A4AB]">{new Date(t.created_at).toLocaleString("en-IN")}</div>
                    </div>
                    <div className={`text-sm ${t.kind === "credit" ? "text-emerald-400" : "text-red-400"}`}>
                      {t.kind === "credit" ? "+" : "-"}₹{t.amount}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
